import { PagedMessageResponse } from "app/api/types/PagedMessageResponse";
import { DetalhesComissoes, FiltroListarIndicador, Indicadores } from "app/types/comissoes/Indicadores";
import { tratamentoErroPadrao } from "app/use-cases";
import { definirIndicador, detalharComissoesPorIndicador } from "app/use-cases/orcamento/comissoes/detalharComissoesPorIndicador";
import React, { useEffect, useRef, useState } from "react";
import microuniversoApi from "../../../api/microuniversoApi";
import globalMU from "../../../../globalMU";
import { useAppSelector } from "../../../redux/hooks";
import { IOrcamento } from "../../../types/IOrcamento";
import { renderizarComponentReact } from "../../renderizarOuRemoverComponentReact";
import { DadosAbaIndicadores, PesquisaIndicador } from "./PesquisaIndicadores";
import { TelaCadastroIndicadoresContainer } from "../cadastro-indicador/TelaCadastroIndicadoresContainer";
import { obterPermissao } from "app/use-cases/obterPermissao";
import { obterParametro } from "app/use-cases/parametro/obterParametro";

export type IndicadoresContainerProps = {
    exibeDetalhesComissoes: (valor: boolean) => void;
    onAlteradoDadosIndicadores: (dados: DadosAbaIndicadores) => void;
}

export const IndicadoresContainer = (props: IndicadoresContainerProps & DadosAbaIndicadores) => {
    const orcamento: IOrcamento = useAppSelector((store) => store.orcamento);
    const [filtroIndicadores, setFiltroIndicadores] = useState<FiltroListarIndicador>(props.abaIndicadores);
    const [indicadores, setIndicadores] = useState<Partial<PagedMessageResponse<Indicadores>>>(props.indicadores);
    const [podeCadastrarNovo, setPodeCadastrarNovo] = useState(false);
    const [exibirRazaoSocial, setExibirRazaoSocial] = useState(false);
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    const permitidoAcaoFuncionalidade = async (
        siglaFuncionalidade: string,
        acao: string
    ) => await obterPermissao({ funcionalidade: siglaFuncionalidade, acao });

    const atualizaAcaoFuncionalidadeBotaoSalvar = async () => {
        const podeCadastrar = await permitidoAcaoFuncionalidade(
            "ORCAMENTO_INDICADORES",
            "INCLUIR"
        );
        if (isMounted.current) {
            setPodeCadastrarNovo(podeCadastrar);
        }
    };

    const carregarParametroIgnorarVinculo = async () => {
        try {
            const valor = await obterParametro({ nome: "IGNORAR_INDICADOR_VINCULADO" });
            if (valor?.toUpperCase() === "S" && isMounted.current) {
                setFiltroIndicadores((prev) => ({ ...prev, Todos: true }));
            }
        } catch {
            // parâmetro não encontrado — mantém comportamento padrão
        }
    };

    const carregarParametroRazaoSocial = async () => {
        try {
            const valor = await obterParametro({ nome: "VISUALIZA_RAZAO_SOCIAL_NA_PROCURA" });
            if (isMounted.current) {
                setExibirRazaoSocial(valor?.toUpperCase() === "S");
            }
        } catch {
            // parâmetro não encontrado — campo Razão Social não exibido
        }
    };

    const onPesquisarIndicador = async (filtros: FiltroListarIndicador) => {
        try {
            globalMU.blockUI();
            setFiltroIndicadores({ ...filtros, IdentificadorOrcamento: orcamento.IdentificadorOrcamento });
            const clientesPesquisados = await microuniversoApi.Orcamento.listarIndicadores({
                CpfCnpj: filtros?.CpfCnpj,
                Nome: filtros?.Nome ?? "",
                NumeroPagina: filtros?.NumeroPagina ?? 1,
                TamanhoPagina: filtros?.TamanhoPagina ?? 20,
                Todos: filtros?.Todos ?? false,
                IdentificadorOrcamento: orcamento.IdentificadorOrcamento,
                // PA 462196 - novos filtros
                Codigo: filtros?.Codigo,
                InscrEstadual: filtros?.InscrEstadual,
                RazaoSocial: filtros?.RazaoSocial,
            });
            if (isMounted.current) {
                setIndicadores(clientesPesquisados);
            }
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    };

    const onAplicarComissao = async (dadosComissoes: DetalhesComissoes) => {
        try {
            globalMU.blockUI();
            await definirIndicador(dadosComissoes);
            globalMU.showSuccessMessage("Indicador definido com sucesso.");
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    };

    const onDetalharComissao = async (codigoIndicador: number) => {
        try {
            globalMU.blockUI();
            const auxDadosComissoes = await detalharComissoesPorIndicador(codigoIndicador);
            props.exibeDetalhesComissoes(true);
            onAplicarComissao(auxDadosComissoes);
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    };

    const onNovo = () => {
        if (orcamento.CodigoCliente != undefined && orcamento.CodigoCliente > 7) {
            renderizarComponentReact(<TelaCadastroIndicadoresContainer codigoIndicador={null} />, "conteudo-pagina", true);
        } else {
            globalMU.showErrorMessage("Para usar o Indicador, será necessário informar o cliente antes.");
        }
    };

    useEffect(() => {
        atualizaAcaoFuncionalidadeBotaoSalvar();
        carregarParametroIgnorarVinculo();    // PA 399931
        carregarParametroRazaoSocial();       // PA 462196
    }, []);

    return (
        <>
            <PesquisaIndicador
                exibirBotaoNovo={podeCadastrarNovo}
                somenteLeitura={orcamento.SomenteLeitura}
                onAlteradoDadosIndicadores={props.onAlteradoDadosIndicadores}
                abaIndicadores={filtroIndicadores}
                indicadores={indicadores}
                onPesquisarIndicador={onPesquisarIndicador}
                onNovo={onNovo}
                onClickDados={onDetalharComissao}
                exibirRazaoSocial={exibirRazaoSocial}
            />
            {<section style={{ marginBottom: "100px" }} />}
        </>
    );
};
